import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Button,
  Input,
  LastUpdated,
  Badge,
  Icon,
  showToast,
} from '@repo/ui';
import type {
  ConfigModalProps,
  ClioFormData,
  ClioWebhookSubscription,
} from '@xtnd/web/shared/types/tenantSettingTypes';
import { getDefaultClioWebhookSubscriptions } from '@xtnd/web/features/webhooks/constants';
import { clioIntegrationApi } from '@xtnd/web/features/settings/api/clioIntegrationApi';

export const ClioConfigModal: React.FC<ConfigModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData,
}) => {
  const [formData, setFormData] = useState<ClioFormData>({
    appId: '',
    clientId: '',
    clientSecret: '',
    authorizationCode: '',
    enableWebhooks: false,
    webhookSubscriptions: getDefaultClioWebhookSubscriptions(),
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Partial<ClioFormData>>({});
  const [isGeneratingOAuthUrl, setIsGeneratingOAuthUrl] = useState(false);
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [clioConfig, setClioConfig] = useState<{
    configured: boolean;
    hasAccessToken: boolean;
    hasRefreshToken: boolean;
    clientId: string;
    tokenExpiresAt?: string;
  } | null>(null);
  const [currentConfig, setCurrentConfig] = useState<{
    appId: string;
    clientId: string;
    clientSecret: string | null;
    authorizationCode: string | null;
    baseUrl: string;
    redirectUri: string;
    webhooksConfigured: boolean;
    webhookSubscriptions: ClioWebhookSubscription[];
  } | null>(null);

  // Helper function to merge existing webhook subscriptions with all available models
  const mergeWebhookSubscriptions = (existingSubscriptions?: ClioWebhookSubscription[]) => {
    const defaultSubscriptions = getDefaultClioWebhookSubscriptions();

    if (!existingSubscriptions || existingSubscriptions.length === 0) {
      return defaultSubscriptions;
    }

    // Create a map of existing subscriptions by modelName
    const existingMap = new Map(existingSubscriptions.map(sub => [sub.modelName, sub]));

    // Merge: use existing config if available, otherwise use default
    return defaultSubscriptions.map(
      defaultSub => existingMap.get(defaultSub.modelName) || defaultSub
    );
  };

  // Fetch current Clio configuration status
  const fetchClioConfig = async () => {
    try {
      const response = await clioIntegrationApi.getConfigStatus();
      setClioConfig(response);
    } catch (error) {
      console.error('Failed to fetch Clio config:', error);
      // Don't show error toast for this as it might be normal if not configured yet
    }
  };

  // Initialize form when modal opens or data changes
  useEffect(() => {
    if (isOpen) {
      fetchClioConfig();

      if (initialData) {
        // Fetch and resolve the actual client ID value
        const resolveClientId = async () => {
          try {
            const response = await clioIntegrationApi.getConfig();
            setCurrentConfig(response);

            setFormData({
              appId: response.appId || '',
              clientId: response.clientId || '',
              clientSecret: '', // Never pre-fill secrets
              authorizationCode: '', // Never pre-fill auth codes
              enableWebhooks: initialData.webhooksConfigured || false,
              webhookSubscriptions: mergeWebhookSubscriptions(initialData.webhookSubscriptions),
            });
          } catch {
            // Silent error handling - fallback to empty client ID
            setCurrentConfig(null);
            setFormData({
              appId: '',
              clientId: '',
              clientSecret: '',
              authorizationCode: '',
              enableWebhooks: initialData.webhooksConfigured || false,
              webhookSubscriptions: mergeWebhookSubscriptions(initialData.webhookSubscriptions),
            });
          }
        };

        resolveClientId();
      } else {
        setFormData({
          appId: '',
          clientId: '',
          clientSecret: '',
          authorizationCode: '',
          enableWebhooks: false,
          webhookSubscriptions: mergeWebhookSubscriptions(),
        });
      }
    }
  }, [isOpen, initialData]);

  const validateForm = (): boolean => {
    const newErrors: Partial<ClioFormData> = {};

    if (!formData.clientId.trim()) {
      newErrors.clientId = 'Client ID is required';
    }

    // Only require client secret if it's not set yet
    const isClientSecretSet = currentConfig?.clientSecret === '[SET]';
    if (!isClientSecretSet && !formData.clientSecret.trim()) {
      newErrors.clientSecret = 'Client Secret is required';
    }

    // Only require auth code if it's not set yet
    const isAuthCodeSet = currentConfig?.authorizationCode === '[SET]';
    if (!isAuthCodeSet && !formData.authorizationCode.trim()) {
      newErrors.authorizationCode = 'Authorization Code is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        appId: '',
        clientId: '',
        clientSecret: '',
        authorizationCode: '',
        enableWebhooks: false,
        webhookSubscriptions: mergeWebhookSubscriptions(),
      });
      setErrors({});
      onClose();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      // Only include sensitive fields if they were actually entered
      const { clientSecret, authorizationCode, ...baseData } = formData;
      const dataToSave = {
        ...baseData,
        // Only include secrets if they have values
        ...(clientSecret.trim() && { clientSecret }),
        ...(authorizationCode.trim() && { authorizationCode }),
      };

      await onSave(dataToSave as ClioFormData);
      handleClose();
    } catch {
      // Silent error handling - error already shown by mutation
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof ClioFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Generate OAuth authorization URL and open popup
  const handleAuthorizeApp = async () => {
    if (!formData.clientId.trim()) {
      showToast.error('Please enter Client ID first');
      return;
    }

    // Only require client secret if it's not already saved
    const isClientSecretSet = currentConfig?.clientSecret === '[SET]';
    if (!isClientSecretSet && !formData.clientSecret.trim()) {
      showToast.error('Please enter Client Secret first');
      return;
    }

    setIsGeneratingOAuthUrl(true);
    try {
      // Generate OAuth URL
      const redirectUri = `${window.location.origin}/clio-redirect`;
      const requestData: { clientId: string; redirectUri: string; clientSecret?: string } = {
        clientId: formData.clientId,
        redirectUri,
      };

      // Only include client secret if it's not already saved or if user entered a new one
      if (!isClientSecretSet || formData.clientSecret.trim()) {
        requestData.clientSecret = formData.clientSecret;
      }

      const response = await clioIntegrationApi.generateOAuthUrl(requestData);

      // Store credentials temporarily for the callback
      // Note: clientSecret is no longer needed in sessionStorage since backend retrieves from SSM
      sessionStorage.setItem(
        'clio-oauth-config',
        JSON.stringify({
          clientId: formData.clientId,
          clientSecret: '', // Empty since backend will retrieve from SSM
          state: response.state,
        })
      );

      // Open authorization URL in popup
      const authWindow = window.open(
        response.authUrl,
        'clio-auth',
        'width=600,height=700,scrollbars=yes,resizable=yes'
      );

      if (!authWindow) {
        throw new Error('Failed to open authorization window. Please allow popups for this site.');
      }

      // Listen for the authorization callback
      const handleMessage = async (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;

        if (event.data.type === 'CLIO_AUTH_SUCCESS') {
          try {
            const { code, state } = event.data;

            // Verify state matches
            const storedConfig = JSON.parse(sessionStorage.getItem('clio-oauth-config') || '{}');
            if (state !== storedConfig.state) {
              throw new Error('Invalid authorization state');
            }

            // Exchange code for tokens
            await clioIntegrationApi.exchangeOAuthCode({
              code,
              state,
              clientId: storedConfig.clientId,
              clientSecret: '', // Empty since backend retrieves from SSM
            });

            // Update form data with the authorization code (for display purposes)
            setFormData(prev => ({ ...prev, authorizationCode: code }));

            // Refresh config to show updated status
            await fetchClioConfig();

            showToast.success('Clio authorization successful! Tokens have been stored securely.');
            authWindow.close();
          } catch (error) {
            showToast.error(
              'Failed to complete authorization: ' +
                (error instanceof Error ? error.message : 'Unknown error')
            );
            authWindow.close();
          } finally {
            sessionStorage.removeItem('clio-oauth-config');
            window.removeEventListener('message', handleMessage);
            setIsGeneratingOAuthUrl(false);
          }
        } else if (event.data.type === 'CLIO_AUTH_ERROR') {
          showToast.error('Authorization failed: ' + (event.data.error || 'Unknown error'));
          authWindow.close();
          window.removeEventListener('message', handleMessage);
          setIsGeneratingOAuthUrl(false);
          sessionStorage.removeItem('clio-oauth-config');
        }
      };

      window.addEventListener('message', handleMessage);

      // Check if window is closed manually
      const checkClosed = setInterval(() => {
        if (authWindow.closed) {
          clearInterval(checkClosed);
          window.removeEventListener('message', handleMessage);
          setIsGeneratingOAuthUrl(false);
          sessionStorage.removeItem('clio-oauth-config');
        }
      }, 1000);
    } catch (error) {
      showToast.error(
        'Failed to generate authorization URL: ' +
          (error instanceof Error ? error.message : 'Unknown error')
      );
      setIsGeneratingOAuthUrl(false);
    }
  };

  // Test connection using stored tokens
  const handleTestConnection = async () => {
    setIsTestingConnection(true);
    try {
      const response = await clioIntegrationApi.testConnection();
      showToast.success(
        `Connection successful! Connected as: ${response.user.name} (${response.user.account?.name || 'Unknown Account'})`
      );
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || error?.message || 'Connection test failed';
      showToast.error(`Connection failed: ${errorMessage}`);
    } finally {
      setIsTestingConnection(false);
    }
  };

  const isClientSecretSet = currentConfig?.clientSecret === '[SET]';
  const isAuthCodeSet = currentConfig?.authorizationCode === '[SET]';

  return (
    <Dialog open={isOpen} onClose={handleClose} size='lg'>
      <form onSubmit={handleSubmit} className='max-h-[90vh] flex flex-col min-h-0'>
        <DialogHeader>
          <div className="flex items-center justify-between content-center w-full pr-8">
            <div>
              <h2 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-white">
                Configure Clio API Integration
              </h2>
              <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-200">
                Set up your Clio API credentials
              </p>
            </div>
            {initialData?.updatedAt && <LastUpdated timestamp={initialData.updatedAt} />}
          </div>
        </DialogHeader>

        <DialogBody className="space-y-6 flex-1 overflow-y-auto pr-2 -mr-2 min-h-0">
          {/* Current Authorization Status */}
          {clioConfig && (
            <div
              className={`rounded-lg p-4 border ${
                clioConfig.configured
                  ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                  : 'bg-xtnd-dark-50 dark:bg-xtnd-dark-700 border-xtnd-dark-100 dark:border-xtnd-dark-600'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                {clioConfig.configured ? (
                  <Icon
                    name="HiCheckCircle"
                    className="h-5 w-5 text-green-600 dark:text-green-400"
                  />
                ) : (
                  <Icon name="HiXCircle" className="h-5 w-5 text-xtnd-dark-400 dark:text-xtnd-dark-300" />
                )}
                <h3
                  className={`text-sm font-medium ${
                    clioConfig.configured
                      ? 'text-green-800 dark:text-green-200'
                      : 'text-xtnd-dark-500 dark:text-xtnd-dark-200'
                  }`}
                >
                  {clioConfig.configured ? 'Authorization Complete' : 'Authorization Required'}
                </h3>
              </div>
              <div className="text-xs space-y-1 text-xtnd-dark-500 dark:text-xtnd-dark-300">
                <div className="flex items-center gap-2">
                  <span>Access Token:</span>
                  <Badge variant={clioConfig.hasAccessToken ? 'success' : 'secondary'}>
                    {clioConfig.hasAccessToken ? 'Available' : 'Missing'}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <span>Refresh Token:</span>
                  <Badge variant={clioConfig.hasRefreshToken ? 'success' : 'secondary'}>
                    {clioConfig.hasRefreshToken ? 'Available' : 'Missing'}
                  </Badge>
                </div>
                {clioConfig.tokenExpiresAt && (
                  <div className="text-xs mt-1">
                    Token expires: {new Date(clioConfig.tokenExpiresAt).toLocaleString()}
                  </div>
                )}
              </div>
              {clioConfig.configured && (
                <div className="mt-3 pt-3 border-t border-green-200 dark:border-green-800">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleTestConnection}
                    loading={isTestingConnection}
                    disabled={isSubmitting}
                    className="text-green-700 dark:text-green-300 border-green-300 dark:border-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
                  >
                    {isTestingConnection ? 'Testing...' : 'Test Connection'}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* API Credentials */}
          <div className="flex flex-col gap-3">
            <Input
              id="appId"
              value={formData.appId}
              label="App ID"
              helperText="Clio Application ID from your registered app"
              onChange={e => handleInputChange('appId', e.target.value)}
              placeholder="Enter Clio Application ID"
              error={errors.appId}
              disabled={isSubmitting}
              fullWidth
            />
            <Input
              id="clientId"
              value={formData.clientId}
              label="Client ID"
              onChange={e => handleInputChange('clientId', e.target.value)}
              placeholder="Enter Clio Client ID"
              error={errors.clientId}
              disabled={isSubmitting}
              fullWidth
              required
            />

            <Input
              id="clientSecret"
              type="password"
              label="Client Secret"
              helperText="Leave blank to keep current value, or enter new value to update."
              value={formData.clientSecret}
              onChange={e => handleInputChange('clientSecret', e.target.value)}
              placeholder={isClientSecretSet ? '••••••••••••••••••••' : 'Enter Clio Client Secret'}
              error={errors.clientSecret}
              disabled={isSubmitting}
              fullWidth
              required={!isClientSecretSet}
            />

            {/* Authorize XTND App Button */}

            <div className="mt-4 p-4 bg-xtnd-light-300 dark:bg-xtnd-dark-900 rounded-lg">
              <div className="flex items-center justify-between">
                <div className=" bg-xtnd-light-300 dark:bg-xtnd-dark-900 rounded-lg">
                  <h4 className="text-sm font-medium text-xtnd-dark-900 dark:text-xtnd-white">
                    Authorize XTND App
                  </h4>
                  <p className="text-xs text-xtnd-dark-500 mt-1 dark:text-xtnd-dark-300">
                    Click to automatically generate authorization code and exchange for tokens
                  </p>
                </div>
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleAuthorizeApp}
                  loading={isGeneratingOAuthUrl}
                  disabled={
                    isSubmitting ||
                    !formData.clientId.trim() ||
                    (!isClientSecretSet && !formData.clientSecret.trim())
                  }
                  leftIcon={<Icon name="HiExternalLink" size="sm" />}
                >
                  {isGeneratingOAuthUrl ? 'Authorizing...' : 'Authorize App'}
                </Button>
              </div>
              {clioConfig?.configured && (
                <div className="text-xs text-green-600 dark:text-green-400 mt-2">
                  ✓ App is already authorized with valid tokens
                </div>
              )}
            </div>

            {/* Authorization Code - moved below the Authorize section */}
            <Input
              id="authorizationCode"
              type="password"
              label="Authorization Code"
              helperText="Leave blank to keep current value, or enter new value to update. This will be auto-filled when you authorize the app."
              value={formData.authorizationCode}
              onChange={e => handleInputChange('authorizationCode', e.target.value)}
              placeholder={isAuthCodeSet ? '••••••••••••••••••••' : 'Enter Clio Authorization Code'}
              error={errors.authorizationCode}
              disabled={isSubmitting}
              fullWidth
              required={!isAuthCodeSet}
            />

            {/* Tokens fields removed; presence is shown in the status badges above */}
          </div>
          {/* Webhook Configuration removed from this modal; managed in Webhook Settings page */}
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={isSubmitting} disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
};
